/**
 * Mobile OAuth Handler
 * Handles Google OAuth specifically for mobile browsers and webviews
 */

export interface MobileEnvironment {
  isMobile: boolean;
  isWebView: boolean;
  isEdgeMobile: boolean;
  isChrome: boolean;
  isSafari: boolean;
  isSecureBrowser: boolean;
}

/**
 * Comprehensive mobile browser detection
 */
export const detectMobileEnvironment = (): MobileEnvironment => {
  const userAgent = navigator.userAgent || '';
  
  const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  const isWebView = /wv|WebView|(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)|FB_IAB|FBAN|FBAV|LinkedIn|Instagram|Twitter|Pinterest/i.test(userAgent);
  const isEdgeMobile = /EdgA|EdgiOS/i.test(userAgent);
  const isChrome = /Chrome/i.test(userAgent) && !/Edge/i.test(userAgent);
  const isSafari = /Safari/i.test(userAgent) && !/Chrome|Edge/i.test(userAgent);
  
  // Consider a browser secure if it's Chrome, Safari, or Edge on mobile
  const isSecureBrowser = isChrome || isSafari || isEdgeMobile;
  
  console.log('[MobileOAuth] Environment Detection:', {
    userAgent: userAgent.substring(0, 100) + '...',
    isMobile,
    isWebView,
    isEdgeMobile,
    isChrome,
    isSafari,
    isSecureBrowser
  });
  
  return {
    isMobile,
    isWebView,
    isEdgeMobile,
    isChrome,
    isSafari,
    isSecureBrowser
  };
};

/**
 * Get user-friendly browser recommendations for mobile OAuth
 */
export const getMobileBrowserRecommendation = (env: MobileEnvironment): string => {
  if (!env.isMobile) {
    return '';
  }
  
  if (env.isWebView) {
    return 'You\'re using an in-app browser. For Google login, please:\n1. Open this page in Chrome or Safari\n2. Or allow opening in system browser when prompted';
  }
  
  if (!env.isSecureBrowser) {
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    
    if (isAndroid) {
      return 'For the best Google login experience, please use Chrome browser.';
    } else if (isIOS) {
      return 'For the best Google login experience, please use Safari or Chrome.';
    }
    
    return 'Please use Chrome, Safari, or another modern browser for Google login.';
  }
  
  return '';
};

/**
 * Handle mobile OAuth redirect with fallback strategies
 */
export const handleMobileOAuthRedirect = async (
  oauthUrl: string,
  env: MobileEnvironment
): Promise<void> => {
  console.log('[MobileOAuth] Handling redirect for URL:', oauthUrl);
  
  // Validate OAuth URL - check for Supabase OAuth patterns
  if (!oauthUrl || typeof oauthUrl !== 'string') {
    throw new Error('Invalid OAuth URL provided: URL is empty or not a string');
  }
  
  // Check for valid OAuth URL patterns (Supabase OAuth URLs contain 'supabase' and 'auth/v1/authorize')
  const isValidOAuthUrl = (
    oauthUrl.includes('supabase') || 
    oauthUrl.includes('auth/v1/authorize') ||
    oauthUrl.includes('accounts.google.com') ||
    oauthUrl.includes('oauth2')
  );
  
  if (!isValidOAuthUrl) {
    console.error('[MobileOAuth] Invalid OAuth URL received:', oauthUrl);
    throw new Error('Invalid OAuth URL provided');
  }
  
  // For webviews, try to open in system browser
  if (env.isWebView) {
    console.log('[MobileOAuth] WebView detected - attempting system browser');
    
    // Try multiple methods to open in system browser
    try {
      // Method 1: Try window.open with _system target (works in some webviews)
      const systemWindow = window.open(oauthUrl, '_system');
      
      // If window.open returns null, it might have been blocked
      if (!systemWindow) {
        console.log('[MobileOAuth] window.open blocked, using location.href');
        window.location.href = oauthUrl;
        return;
      }
      
      // Small delay to see if window opened
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Check if we can detect the system browser opened
      if (systemWindow.closed !== false) {
        console.log('[MobileOAuth] System browser may have opened');
        return;
      }
      
    } catch (_error) {
      console.log('[MobileOAuth] System browser redirect failed, using fallback');
      window.location.href = oauthUrl;
    }
    
    return;
  }
  
  // For mobile browsers that might have popup blockers
  if (env.isMobile) {
    console.log('[MobileOAuth] Mobile browser redirect');
    
    // Add a small delay for better UX on mobile
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Direct redirect for mobile browsers
    window.location.href = oauthUrl;
    return;
  }
  
  // Default redirect for desktop or unknown environments
  window.location.href = oauthUrl;
};

/**
 * Show mobile-friendly OAuth instructions
 */
export const showMobileOAuthInstructions = (env: MobileEnvironment): boolean => {
  const recommendation = getMobileBrowserRecommendation(env);
  
  if (!recommendation) {
    return true; // Proceed normally
  }
  
  // Show instructions in a user-friendly way
  const shouldProceed = window.confirm(
    `${recommendation}\n\nDo you want to continue with Google login?`
  );
  
  return shouldProceed;
};

/**
 * Enhanced mobile OAuth error handler
 */
export const handleMobileOAuthError = (error: any, env: MobileEnvironment): string => {
  console.error('[MobileOAuth] Error:', error);
  
  const errorMessage = error?.message || '';
  
  // Google-specific error messages
  if (errorMessage.includes('secure browser') || errorMessage.includes('disallowed_useragent')) {
    const recommendation = getMobileBrowserRecommendation(env);
    return recommendation || 'Please use a secure browser for Google login.';
  }
  
  if (errorMessage.includes('popup_blocked')) {
    return 'Popup blocked. Please allow popups for this site or try again.';
  }
  
  if (errorMessage.includes('access_blocked')) {
    if (env.isWebView) {
      return 'Login blocked in app browser. Please open this page in Chrome or Safari.';
    }
    return 'Access blocked. Please try using Chrome or Safari browser.';
  }
  
  if (errorMessage.includes('network') || errorMessage.includes('connection')) {
    return 'Network error. Please check your internet connection and try again.';
  }
  
  // Generic mobile-friendly error message
  if (env.isMobile && !env.isSecureBrowser) {
    return 'Login failed. Please try using Chrome or Safari browser.';
  }
  
  return errorMessage || 'Google login failed. Please try again.';
};

/**
 * Pre-flight check for mobile OAuth compatibility
 */
export const checkMobileOAuthCompatibility = (): {
  compatible: boolean;
  warning?: string;
  recommendation?: string;
} => {
  const env = detectMobileEnvironment();
  
  // Check for known problematic environments
  if (env.isWebView) {
    return {
      compatible: false,
      warning: 'In-app browser detected',
      recommendation: 'Please open this page in Chrome or Safari for Google login'
    };
  }
  
  if (env.isMobile && !env.isSecureBrowser) {
    return {
      compatible: true,
      warning: 'Unsupported browser detected',
      recommendation: getMobileBrowserRecommendation(env)
    };
  }
  
  return { compatible: true };
};